import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import "../css/Admin_css/admin-customize.css";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import {
  STATUS_OPTIONS,
  PAYROLL_ATTENDANCE_FIELD_KEYS,
  mdl,
  payrollType,
  headerMetaGrid,
  headerMetaPill,
  headerMetaLabel,
  buildFormFromRecord,
  normalizeProrataBasis,
  numOrZero,
  formatINR,
  formatYearMonth,
  Section,
  DaysAttendanceSection,
  SalaryRulesSection,
  CalculationsSection,
  SalaryComponentsSection,
  PayrollSummarySection,
  StaffPayrollHeaderChip,
  HeaderLockAction,
  HeaderSyncAttendanceAction,
  computePayrollAmounts,
  buildPayrollCalculationItems,
  computeLopAmount,
  formInputStyle,
  entityBoxStyle,
  getPayrollModalStyles,
  payRollSalaryApiKey,
  payRollSalaryRecordMatches,
} from "./payrollMonthlyShared";

const MdlMonthlyPayRollDetails = ({
  show = false,
  actionClose,
  staffRecord = null,
    onSaved,
  onRecordPatched,
}) => {
  const [modalShow, setModalShow] = useState(show);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [syncAttendanceBusy, setSyncAttendanceBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [lockMessage, setLockMessage] = useState("");
  const [salaryComponents, setSalaryComponents] = useState(null);
  const [salaryComponentsLoading, setSalaryComponentsLoading] = useState(false);
  const [salaryComponentsError, setSalaryComponentsError] = useState("");
  const [componentActionError, setComponentActionError] = useState("");
  const [componentActionBusy, setComponentActionBusy] = useState(null);

  const isRecordLocked = form
    ? form.is_locked
    : staffRecord
      ? Number(staffRecord.is_locked) === 1
      : false;
  const effectivelyLocked = Boolean(form && isRecordLocked);

  useEffect(() => {
    setModalShow(show);
  }, [show]);

  useEffect(() => {
    if (staffRecord && show) {
      setForm(buildFormFromRecord(staffRecord));
      setSaveError("");
      setLockMessage("");
    }
  }, [staffRecord, show]);

  /** Reload this payroll row from the server (attendance sync, net salary, lock flags). */
  const refreshStaffRecordFromServer = useCallback(async () => {
    if (!staffRecord?.id || !staffRecord?.year_month || !staffRecord?.session_id) {
      return null;
    }
    const schoolCode =
      staffRecord.school_code || SessionManager.getSchoolCode();
    const ym = String(staffRecord.year_month);
    const ymParts = ym.split("-");
    if (ymParts.length < 2) return null;
    const year = parseInt(ymParts[0], 10);
    const month = parseInt(ymParts[1], 10);
    if (!schoolCode || Number.isNaN(year) || Number.isNaN(month)) return null;

    const FUNCTION_NAME = "getPayRollMonthlySalary.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;

    try {
      const res = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: String(staffRecord.session_id),
          month,
          year,
        },
      });
      if (res.data?.api_status !== "OK") return null;
      const rows = res.data.staff_payroll || [];
      const updated = rows.find((row) =>
        payRollSalaryRecordMatches(row, payRollSalaryApiKey(staffRecord))
      );
      if (!updated) return null;
      if (onRecordPatched) onRecordPatched(updated);
      setForm(buildFormFromRecord(updated));
      return updated;
    } catch (e) {
      console.error("refreshStaffRecordFromServer:", e);
      return null;
    }
  }, [staffRecord, onRecordPatched]);

  const fetchSalaryComponents = useCallback(async () => {
    if (
      !show ||
      !payRollSalaryApiKey(staffRecord) ||
      !staffRecord?.staff_id ||
      !staffRecord?.year_month
    ) {
      setSalaryComponents(null);
      setSalaryComponentsError("");
      setSalaryComponentsLoading(false);
      return;
    }

    const FUNCTION_NAME = "getPayRollMonthlyStaffSalaryComponents.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
    const schoolCode = staffRecord.school_code || SessionManager.getSchoolCode();
    const params = {
      salary_id: payRollSalaryApiKey(staffRecord),
      staff_id: String(staffRecord.staff_id),
      year_month: String(staffRecord.year_month),
      school_code: schoolCode,
      session_id:
        staffRecord.session_id != null ? String(staffRecord.session_id) : undefined,
    };

    setSalaryComponentsLoading(true);
    setSalaryComponentsError("");

    try {
      const res = await axios.get(URL, { params });
      if (res.data && res.data.api_status === "OK") {
        setSalaryComponents({
          earnings: res.data.earnings || [],
          deductions: res.data.deductions || [],
          total_earnings: res.data.total_earnings,
          total_deductions: res.data.total_deductions,
          component_master: res.data.component_master || [],
          staff_components: res.data.staff_components || [],
        });
      } else {
        setSalaryComponents(null);
        setSalaryComponentsError(
          (res.data && res.data.api_remark) || "Could not load salary components."
        );
      }
    } catch (e) {
      setSalaryComponents(null);
      setSalaryComponentsError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while loading salary components."
      );
    } finally {
      setSalaryComponentsLoading(false);
    }
  }, [show, staffRecord?.id, staffRecord?.staff_id, staffRecord?.year_month]);

  useEffect(() => {
    fetchSalaryComponents();
  }, [fetchSalaryComponents]);

  const postComponentForm = useCallback(
    async (functionName, fields) => {
      if (!staffRecord) return { ok: false, remark: "No record loaded." };
      const schoolCode =
        staffRecord.school_code || SessionManager.getSchoolCode();
      const sessionId = staffRecord.session_id;
      if (!schoolCode || sessionId == null || !payRollSalaryApiKey(staffRecord)) {
        return { ok: false, remark: "Missing school, session, or record id." };
      }

      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${functionName}`;
      const body = new URLSearchParams();
      body.append("school_code", String(schoolCode));
      body.append("session_id", String(sessionId));
      body.append("salary_id", payRollSalaryApiKey(staffRecord));
      Object.entries(fields).forEach(([k, v]) => {
        body.append(k, v != null ? String(v) : "");
      });

      try {
        const res = await axios.post(URL, body.toString(), {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        if (res.data && res.data.api_status === "OK") {
          return { ok: true, remark: res.data.api_remark || "Saved." };
        }
        return {
          ok: false,
          remark: (res.data && res.data.api_remark) || "Request was not accepted.",
        };
      } catch (e) {
        return {
          ok: false,
          remark:
            e?.response?.data?.api_remark ||
            e?.message ||
            "Network error while saving component.",
        };
      }
    },
    [staffRecord]
  );

  const handleAddSalaryComponent = useCallback(
    async (componentCode, componentType) => {
      if (!staffRecord || effectivelyLocked) return;
      setComponentActionError("");
      setComponentActionBusy("add");
      const result = await postComponentForm("addPayRollStaffSalaryComponent.php", {
        staff_id: staffRecord.staff_id,
        year_month: staffRecord.year_month,
        component_code: componentCode,
        component_type: componentType,
        amount: "0",
        calc_source: "manual",
      });
      setComponentActionBusy(null);
      if (result.ok) {
        await fetchSalaryComponents();
      } else {
        setComponentActionError(result.remark);
      }
    },
    [staffRecord, effectivelyLocked, postComponentForm, fetchSalaryComponents]
  );

  const handleUpdateSalaryComponent = useCallback(
    async (componentId, amount) => {
      if (!staffRecord || effectivelyLocked) return;
      setComponentActionError("");
      setComponentActionBusy(componentId);
      const result = await postComponentForm("updatePayRollStaffSalaryComponent.php", {
        component_id: componentId,
        amount: String(amount),
      });
      setComponentActionBusy(null);
      if (result.ok) {
        await fetchSalaryComponents();
      } else {
        setComponentActionError(result.remark);
      }
    },
    [staffRecord, effectivelyLocked, postComponentForm, fetchSalaryComponents]
  );

  const handleRemoveSalaryComponent = useCallback(
    async (componentId) => {
      if (!staffRecord || effectivelyLocked) return;
      setComponentActionError("");
      setComponentActionBusy(componentId);
      const result = await postComponentForm("deletePayRollStaffSalaryComponent.php", {
        component_id: componentId,
      });
      setComponentActionBusy(null);
      if (result.ok) {
        await fetchSalaryComponents();
      } else {
        setComponentActionError(result.remark);
      }
    },
    [staffRecord, effectivelyLocked, postComponentForm, fetchSalaryComponents]
  );

  const payrollAmounts = useMemo(() => {
    if (!staffRecord || !form) return null;
    return computePayrollAmounts(staffRecord, form, true, salaryComponents);
  }, [staffRecord, form, salaryComponents]);

  const derivedNet = payrollAmounts?.netSalary ?? 0;

  const calculationDisplay = useMemo(() => {
    if (!staffRecord || !form) return null;
    return buildPayrollCalculationItems(
      staffRecord,
      form,
      true,
      salaryComponents,
      payrollAmounts
    );
  }, [staffRecord, form, salaryComponents, payrollAmounts]);

  useEffect(() => {
    if (!form || !payrollAmounts) return;
    setForm((prev) => {
      if (!prev) return prev;
      const g = payrollAmounts.grossEarnings;
      const o = payrollAmounts.otherDeductions;
      if (
        numOrZero(prev.gross_earnings) === g &&
        numOrZero(prev.total_deductions) === o
      ) {
        return prev;
      }
      return { ...prev, gross_earnings: g, total_deductions: o };
    });
  }, [form, payrollAmounts]);

  const handleClose = useCallback(() => {
    setModalShow(false);
    setSaveError("");
    if (actionClose) actionClose();
  }, [actionClose]);

  const patchForm = useCallback((key, val) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [key]: val };
      if (PAYROLL_ATTENDANCE_FIELD_KEYS.includes(key)) {
        next.is_fetch_attendance = false;
      }
      return next;
    });
  }, []);

  const applyLockState = useCallback(
    (locked) => {
      const lockedVal = !!locked;
      setForm((prev) => (prev ? { ...prev, is_locked: lockedVal } : prev));
      if (onRecordPatched) {
        onRecordPatched({ is_locked: lockedVal ? 1 : 0 });
      }
    },
    [onRecordPatched]
  );

  const postLockStatus = useCallback(
    async (nextLocked) => {
      if (!staffRecord) return { ok: false, remark: "No record loaded." };
      const schoolCode =
        staffRecord.school_code || SessionManager.getSchoolCode();
      const sessionId = staffRecord.session_id;
      if (!schoolCode || sessionId == null || !payRollSalaryApiKey(staffRecord)) {
        return { ok: false, remark: "Missing school, session, or record id." };
      }

      const FUNCTION_NAME = "setPayRollMonthlyStaffSalaryLock.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
      const body = new URLSearchParams();
      body.append("school_code", String(schoolCode));
      body.append("session_id", String(sessionId));
      body.append("salary_id", payRollSalaryApiKey(staffRecord));
      body.append("is_locked", nextLocked ? "1" : "0");

      try {
        const res = await axios.post(URL, body.toString(), {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        if (res.data && res.data.api_status === "OK") {
          return {
            ok: true,
            remark: res.data.api_remark || (nextLocked ? "Record locked." : "Record unlocked."),
          };
        }
        return {
          ok: false,
          remark: (res.data && res.data.api_remark) || "Lock update was not accepted.",
        };
      } catch (e) {
        return {
          ok: false,
          remark:
            e?.response?.data?.api_remark ||
            e?.message ||
            "Network error while updating lock.",
        };
      }
    },
    [staffRecord]
  );

  const handleUnlockRecord = useCallback(async () => {
    if (!isRecordLocked) return;
    setLockBusy(true);
    setSaveError("");
    setLockMessage("");
    const result = await postLockStatus(false);
    setLockBusy(false);
    if (result.ok) {
      applyLockState(false);
      const msg = result.remark || "Record unlocked.";
      setLockMessage(msg);
      myToast.notify_success(msg);
      await refreshStaffRecordFromServer();
      if (onSaved) onSaved();
      await fetchSalaryComponents();
    } else {
      setSaveError(result.remark);
    }
  }, [
    isRecordLocked,
    postLockStatus,
    applyLockState,
    onSaved,
    refreshStaffRecordFromServer,
    fetchSalaryComponents,
  ]);

  const handleSave = useCallback(async (options = {}) => {
    const { forceLock = false, closeOnSuccess = true } = options;
    if (!staffRecord || !form) return;
    const schoolCode =
      staffRecord.school_code || SessionManager.getSchoolCode();
    const sessionId = staffRecord.session_id;
    if (!schoolCode || sessionId == null || !payRollSalaryApiKey(staffRecord)) {
      setSaveError("Missing school, session, or record id.");
      return;
    }

    const FUNCTION_NAME = "updatePayRollMonthlyStaffSalary.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;

    const body = new URLSearchParams();
    body.append("school_code", String(schoolCode));
    body.append("session_id", String(sessionId));
    body.append("salary_id", payRollSalaryApiKey(staffRecord));
    body.append("gross_earnings", String(numOrZero(form.gross_earnings)));
    const otherDeductionsSave =
      payrollAmounts?.otherDeductions ?? numOrZero(form.total_deductions);
    const lopAmountSave =
      payrollAmounts?.lopAmount ?? computeLopAmount(staffRecord, form, true);
    const totalDeductionsSave =
      payrollAmounts?.totalDeductions ??
      Math.round((otherDeductionsSave + lopAmountSave) * 100) / 100;
    body.append("total_deductions", String(totalDeductionsSave));
    body.append("working_days", String(numOrZero(form.working_days)));
    body.append("payable_days", String(numOrZero(form.payable_days)));
    body.append("staff_present", String(numOrZero(form.staff_present)));
    body.append("staff_absent", String(numOrZero(form.staff_absent)));
    body.append("staff_leave", String(numOrZero(form.staff_leave)));
    body.append("paid_leave", String(numOrZero(form.paid_leave)));
    body.append("half_day", String(numOrZero(form.half_day)));
    body.append("late_day", String(numOrZero(form.late_day)));
    body.append("week_off", String(numOrZero(form.week_off)));
    body.append("holiday", String(numOrZero(form.holiday)));
    body.append("status", String(form.status));
    body.append("remarks", form.remarks != null ? String(form.remarks) : "");
    body.append("is_locked", forceLock || form.is_locked ? "1" : "0");
    body.append("is_fetch_attendance", form.is_fetch_attendance ? "1" : "0");
    if (staffRecord.salary_rules?.id != null && staffRecord.salary_rules.id !== "") {
      body.append("salary_rules_id", String(staffRecord.salary_rules.id));
      body.append("staff_id", String(staffRecord.staff_id ?? ""));
      body.append("prorata_basis", normalizeProrataBasis(form.prorata_basis));
    }

    setSaving(true);
    setSaveError("");
    try {
      const res = await axios.post(URL, body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data && res.data.api_status === "OK") {
        const lockedNow = forceLock || form.is_locked;
        applyLockState(lockedNow);
        await refreshStaffRecordFromServer();
        if (onSaved) onSaved();
        await fetchSalaryComponents();
        const successMsg = lockedNow
          ? "Record saved and locked."
          : "Changes saved successfully.";
        myToast.notify_success(successMsg);
        if (closeOnSuccess) {
          handleClose();
        } else {
          setLockMessage(successMsg);
        }
      } else {
        setSaveError(
          (res.data && res.data.api_remark) || "Update was not accepted."
        );
      }
    } catch (e) {
      setSaveError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while saving."
      );
    } finally {
      setSaving(false);
    }
  }, [
    staffRecord,
    form,
    payrollAmounts,
    onSaved,
    handleClose,
    applyLockState,
    refreshStaffRecordFromServer,
    fetchSalaryComponents,
  ]);

  const handleLockRecord = useCallback(async () => {
    if (isRecordLocked || !form) return;
    setSaveError("");
    setLockMessage("");
    await handleSave({ forceLock: true, closeOnSuccess: false });
  }, [isRecordLocked, form, handleSave]);

  const busy = saving || lockBusy || syncAttendanceBusy;

  const applyAttendanceToForm = useCallback(
    (attendance) => {
      if (!attendance) return;
      const patch = {
        staff_present: numOrZero(attendance.staff_present),
        staff_absent: numOrZero(attendance.staff_absent),
        staff_leave: numOrZero(attendance.staff_leave),
        paid_leave: numOrZero(attendance.paid_leave),
        half_day: numOrZero(attendance.half_day),
        late_day: numOrZero(attendance.late_day),
        is_fetch_attendance: 1,
      };
      setForm((prev) => (prev ? { ...prev, ...patch, is_fetch_attendance: true } : prev));
      if (onRecordPatched) {
        onRecordPatched(patch);
      }
    },
    [onRecordPatched]
  );

  const handleSyncAttendance = useCallback(async () => {
    if (!staffRecord || isRecordLocked) return;
    const schoolCode =
      staffRecord.school_code || SessionManager.getSchoolCode();
    const sessionId = staffRecord.session_id;
    if (!schoolCode || sessionId == null || !payRollSalaryApiKey(staffRecord)) {
      setSaveError("Missing school, session, or record id.");
      return;
    }

    setSyncAttendanceBusy(true);
    setSaveError("");
    setLockMessage("");

    const FUNCTION_NAME = "syncPayRollMonthlyStaffAttendance.php";
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
    const body = new URLSearchParams();
    body.append("school_code", String(schoolCode));
    body.append("session_id", String(sessionId));
    body.append("salary_id", payRollSalaryApiKey(staffRecord));

    try {
      const res = await axios.post(URL, body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data?.api_status === "OK" && res.data.attendance) {
        const msg =
          res.data.api_remark ||
          "Attendance loaded from staff attendance. Save changes to update payroll.";
        applyAttendanceToForm(res.data.attendance);
        myToast.notify_success(msg);
        setLockMessage(msg);
      } else {
        setSaveError(
          (res.data && res.data.api_remark) ||
            "Could not load attendance from staff attendance."
        );
      }
    } catch (e) {
      setSaveError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while syncing attendance."
      );
    } finally {
      setSyncAttendanceBusy(false);
    }
  }, [staffRecord, isRecordLocked, applyAttendanceToForm]);

  if (!staffRecord) {
    return null;
  }

  const r = staffRecord;

  return (
    <>
      <style>{getPayrollModalStyles()}</style>
      <Modal
        show={modalShow}
        onHide={handleClose}
        animation={false}
        size="xl"
        dialogClassName="modal-payroll-mdl-wide"
        aria-labelledby="monthly-payroll-details-title"
        centered
        backdrop="static"
        backdropClassName="custom-backdrop"
        contentClassName="custom-modal-content payroll-mdl"
      >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          fontFamily: mdl.font,
          minHeight: 0,
        }}
      >
        <Modal.Header
          closeButton
          className="border-0"
          style={{
            background: `linear-gradient(115deg, ${COLORS.primary_color_shade8 || "#0277BD"} 0%, ${COLORS.primary_color_shade7 || "#01579B"} 28%, ${COLORS.primary_color_shade6 || "#0288d1"} 55%, ${COLORS.primary_color_shade5 || "#29b6f6"} 100%)`,
            color: COLORS.white,
            padding: "20px 24px",
            flexShrink: 0,
            boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.12)",
          }}
        >
          <div className="w-100 pe-4">
          <Modal.Title
            id="monthly-payroll-details-title"
            style={{
                fontSize: 19,
              fontWeight: 750,
              margin: 0,
              letterSpacing: "-0.02em",
              lineHeight: 1.35,
              textShadow: "0 1px 2px rgba(0,0,0,0.12)",
            }}
          >
              Monthly payroll (edit)
          </Modal.Title>
            <div className="mt-2" style={headerMetaGrid}>
              {form ? (
                <>
                  <StaffPayrollHeaderChip
                    label="Name"
                    value={r.staff_name || "â€”"}
                    title={r.staff_name}
                  />
                  <StaffPayrollHeaderChip
                    label="Pay month"
                    value={formatYearMonth(r.year_month)}
                  />
                  <StaffPayrollHeaderChip
                    label="Basic (master)"
                    value={formatINR(r.basic_salary)}
                  />
                  <div style={headerMetaPill}>
                    <div style={headerMetaLabel}>Payroll status</div>
                    <Form.Select
                      value={form.status}
                      onChange={(e) => patchForm("status", e.target.value)}
                      disabled={effectivelyLocked}
                      size="sm"
                      style={{
                        borderRadius: mdl.radiusSm,
                        border: "1px solid rgba(255,255,255,0.35)",
                        background: "rgba(255,255,255,0.95)",
                        fontSize: payrollType.headerValue,
                        fontWeight: 600,
                        padding: "6px 10px",
                        fontFamily: mdl.font,
                      }}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <StaffPayrollHeaderChip
                    label="Locked"
                    value={isRecordLocked ? "Yes" : "No"}
                  />
                  <HeaderLockAction
                    isLocked={isRecordLocked}
                    onLock={handleLockRecord}
                    onUnlock={handleUnlockRecord}
                    busy={busy}
                    saving={saving}
                  />
                  <HeaderSyncAttendanceAction
                    onSync={handleSyncAttendance}
                    busy={syncAttendanceBusy}
                    disabled={isRecordLocked}
                  />
                </>
              ) : null}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body
          style={{
            padding: "8px 20px 12px",
            background: mdl.pageBg,
            overflowY: "auto",
            flex: "1 1 auto",
            maxHeight: "calc(90vh - 180px)",
            fontFamily: mdl.font,
          }}
        >
          {isRecordLocked ? (
            <div
              className="mb-2 py-2 px-3"
              style={{
                background: "linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(251, 191, 36, 0.18) 100%)",
                color: "#92400e",
                fontSize: 14,
                fontWeight: 600,
                border: "1px solid rgba(245, 158, 11, 0.35)",
                borderRadius: mdl.radiusLg,
                fontFamily: mdl.font,
              }}
            >
              This record is locked. Use Unlock record to edit payroll fields.
            </div>
          ) : null}

          {lockMessage ? (
            <div
              className="mb-2 py-2 px-3"
              style={{
                background: "linear-gradient(135deg, rgba(236, 253, 245, 0.95) 0%, rgba(209, 250, 229, 0.55) 100%)",
                color: "#047857",
                fontSize: 14,
                fontWeight: 600,
                border: "1px solid rgba(16, 185, 129, 0.35)",
                borderRadius: mdl.radiusLg,
                fontFamily: mdl.font,
              }}
            >
              {lockMessage}
            </div>
          ) : null}

          {saveError ? (
            <div
              className="mb-2 py-2 px-3"
              style={{
                background: "linear-gradient(135deg, rgba(254, 226, 226, 0.9) 0%, rgba(254, 202, 202, 0.5) 100%)",
                color: "#b91c1c",
                fontSize: 14,
                fontWeight: 600,
                border: "1px solid rgba(248, 113, 113, 0.45)",
                borderRadius: mdl.radiusLg,
                fontFamily: mdl.font,
              }}
            >
              {saveError}
            </div>
          ) : null}

          {form ? (
            <>
              <DaysAttendanceSection
                record={r}
                form={form}
                isEdit
                disabled={effectivelyLocked}
                onPatch={patchForm}
              />

              <SalaryRulesSection
                rules={r.salary_rules}
                isEdit
                prorataValue={form.prorata_basis}
                onProrataChange={(v) => patchForm("prorata_basis", v)}
                disabled={effectivelyLocked}
              />

              <CalculationsSection calculationDisplay={calculationDisplay} />

              <SalaryComponentsSection
                bundle={salaryComponents}
                loading={salaryComponentsLoading}
                error={salaryComponentsError}
                actionError={componentActionError}
                isEdit
                      disabled={effectivelyLocked}
                onAdd={handleAddSalaryComponent}
                onUpdateAmount={handleUpdateSalaryComponent}
                onRemove={handleRemoveSalaryComponent}
                actionBusy={componentActionBusy}
              />

              <PayrollSummarySection
                payrollAmounts={payrollAmounts}
                record={r}
                isEdit
                derivedNet={derivedNet}
              />

              <Section title="Remarks" compact>
                  <Form.Control
                    as="textarea"
                  rows={2}
                    maxLength={255}
                    placeholder="Optional notes"
                    value={form.remarks}
                    onChange={(e) => patchForm("remarks", e.target.value)}
                    disabled={effectivelyLocked}
                  className="mb-0"
                    style={{
                    ...formInputStyle(effectivelyLocked, "remarks"),
                    ...entityBoxStyle("remarks"),
                    padding: "8px 10px",
                      resize: "vertical",
                    minHeight: 52,
                    fontWeight: 500,
                    fontSize: payrollType.fieldInputCompact,
                    }}
                  />
              </Section>
            </>
          ) : (
            <div
              className="text-center py-5 px-3"
              style={{
                color: mdl.muted,
                fontSize: 14,
                fontFamily: mdl.font,
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              <span
                className="payroll-mdl-spinner d-inline-block rounded-circle mb-3"
                style={{
                  width: 40,
                  height: 40,
                  border: `2px solid ${mdl.border}`,
                  borderTopColor: COLORS.primary_color_shade5 || "#29b6f6",
                }}
                aria-hidden
              />
              Loading…
            </div>
          )}
        </Modal.Body>

        <Modal.Footer
          className="border-0 d-flex flex-wrap justify-content-end gap-2 pt-2 pb-2 px-3"
          style={{
            background: mdl.footerBg,
            borderTop: `1px solid ${mdl.borderHairline}`,
            flexShrink: 0,
          }}
        >
          <>
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={busy}
                className="px-4 fw-semibold"
                style={{
                  borderRadius: mdl.radiusMd,
                  borderColor: "#94a3b8",
                  color: "#334155",
                  background: "#ffffff",
                }}
              >
                Cancel
              </Button>
              {!isRecordLocked ? (
              <Button
                variant="primary"
                  onClick={() => handleSave({ closeOnSuccess: false })}
                  disabled={busy || !form}
                  className="px-4 fw-semibold text-white"
                style={{
                  borderRadius: mdl.radiusMd,
                    minWidth: 140,
                    backgroundColor: "#0d9488",
                    border: "2px solid #0f766e",
                    color: "#ffffff",
                    boxShadow: "0 4px 14px rgba(15, 118, 110, 0.45)",
                }}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
              ) : null}
          </>
        </Modal.Footer>
      </div>
    </Modal>
    </>
  );
};

export default MdlMonthlyPayRollDetails;
